"use client";

import { useEffect } from "react";

/**
 * EPIC-048 STORY-2001 — unconditional service-worker registration.
 *
 * Registers `/sw.js` on every page load (when the browser supports it
 * + the page is served over HTTPS / localhost). The SW's fetch handler
 * is scope-limited to navigation requests only — see comments in
 * `apps/web/public/sw.js`. Worst case a buggy SW falls through to the
 * network entirely; we don't intercept API or static-asset requests.
 *
 * STORY-2004's `WebPushToggle` no longer needs to register the SW
 * itself when this component is mounted — but it stays defensive
 * (registers if the SW isn't already registered) so push subscribe
 * works even when this component fails (private mode, blocked SW, etc.).
 */
export function ServiceWorkerRegister() {
  useEffect(() => {
    if (typeof window === "undefined") return;
    if (!("serviceWorker" in navigator)) return;
    // Only register on https + localhost. file:// pages and
    // staging-with-no-tls would noisily fail otherwise.
    const isSecure =
      window.location.protocol === "https:" ||
      window.location.hostname === "localhost" ||
      window.location.hostname === "127.0.0.1";
    if (!isSecure) return;

    void navigator.serviceWorker
      .register("/sw.js", { scope: "/" })
      .catch((err) => {
        // Soft-fail. The SW is non-essential UX; a registration
        // failure shouldn't surface to the user. Log to console so
        // we still see it in DevTools during dev / staging.
        console.warn("[sw] register failed", err);
      });
  }, []);

  return null;
}
